<template>
  <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb
            :title="page.title"
            :breadcrumbs="breadcrumbs"
         ></BaseBreadcrumb>
         <v-row>
            <v-col cols="12">
              <div>
                <v-card flat>
                  <v-toolbar flat color="white">
                    <v-toolbar-title>Manual Payment Entry</v-toolbar-title>
                    <v-spacer />
                    <v-btn text color="secondary" @click="resetManualForm">Reset</v-btn>
                    <v-btn color="blue darken-3" dark :loading="saving" :disabled="saving" @click="submitManualPayment">
                      <v-icon left small>mdi-content-save</v-icon>
                      Record Payment
                    </v-btn>
                  </v-toolbar>

                  <v-divider />

                  <v-card-text class="pt-5">
                    <v-alert dense text type="info" class="mb-5">
                      Admin-recorded payments are saved as <strong>Success</strong> immediately. Use this form only after payment has been verified by the Secretariat.
                    </v-alert>

                    <v-form ref="manualForm" v-model="manualFormValid" lazy-validation>
                      <div class="text-overline grey--text text--darken-1 mb-2">Member</div>

                      <v-card v-if="manual.selectedMember" outlined class="mb-4 pa-3" color="teal lighten-5">
                        <div class="d-flex align-center">
                          <div class="flex-grow-1">
                            <div class="font-weight-medium">{{ manual.selectedMember.fullname }}</div>
                            <div class="caption grey--text">
                              {{ manual.selectedMember.memberid }}
                              &nbsp;-&nbsp;
                              {{ manual.selectedMember.icpassport }}
                              &nbsp;-&nbsp;
                              User ID: {{ manual.selectedMember.userid }}
                              <span v-if="manual.selectedMember.contactemail">
                                &nbsp;-&nbsp;{{ manual.selectedMember.contactemail }}
                              </span>
                            </div>
                          </div>
                          <v-chip small color="teal" dark class="mr-2">{{ manual.selectedMember.membertype }}</v-chip>
                          <v-chip small color="blue-grey" dark class="mr-2">{{ manual.selectedMember.status_label || 'Member' }}</v-chip>
                          <v-btn icon small @click="clearMemberSelection">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                      </v-card>

                      <v-autocomplete
                        v-if="!manual.selectedMember"
                        v-model="manual.selectedMember"
                        :items="members"
                        :loading="loadingMembers"
                        :search-input.sync="memberSearch"
                        item-text="fullname"
                        item-value="userid"
                        return-object
                        outlined
                        dense
                        clearable
                        label="Search member by name, member ID, or IC/Passport *"
                        placeholder="Start typing to search..."
                        no-data-text="No members found"
                        :filter="memberFilter"
                        :rules="[v => !!v || 'Please select a member']"
                        class="mb-2"
                      >
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>{{ item.fullname }}</v-list-item-title>
                            <v-list-item-subtitle>
                              {{ item.memberid || 'No Member ID yet' }} - {{ item.icpassport || 'No IC/Passport' }} - {{ item.membertype }} - {{ item.status_label || 'Member' }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-chip x-small color="blue-grey" dark>{{ item.status_label || 'Member' }}</v-chip>
                          </v-list-item-action>
                        </template>
                      </v-autocomplete>

                      <v-divider class="mb-5 mt-2" />
                      <div class="text-overline grey--text text--darken-1 mb-3">Payment Details</div>

                      <v-row dense>
                        <v-col cols="12" sm="6" md="3">
                          <v-select
                            v-model="manual.paymenttype"
                            :items="paymentTypeOptions"
                            item-text="label"
                            item-value="value"
                            outlined
                            dense
                            label="Payment For *"
                            hint="Choose the fee category being recorded"
                            persistent-hint
                            :rules="[v => !!v || 'Required']"
                          />
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <v-text-field
                            v-model="manual.payyear"
                            type="number"
                            outlined
                            dense
                            label="Pay Year *"
                            :rules="[v => !!v || 'Required', v => (v >= 2000 && v <= 2100) || 'Invalid year']"
                          />
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <v-text-field
                            v-model="manual.amount"
                            type="number"
                            outlined
                            dense
                            label="Amount (RM) *"
                            prefix="RM"
                            step="0.01"
                            min="0"
                            :rules="[v => !!v || 'Required', v => parseFloat(v) > 0 || 'Must be > 0']"
                          />
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <v-text-field
                            v-model="manual.paymentdate"
                            type="date"
                            outlined
                            dense
                            label="Payment Date *"
                            :rules="[v => !!v || 'Required']"
                          />
                        </v-col>
                      </v-row>

                      <v-divider class="mb-5 mt-1" />
                      <div class="text-overline grey--text text--darken-1 mb-3">Transaction</div>

                      <v-row dense>
                        <v-col cols="12" sm="6">
                          <v-text-field
                            :value="manual.selectedMember ? manual.selectedMember.membertype : ''"
                            outlined
                            dense
                            label="Member Type"
                            readonly
                            filled
                            hint="Auto-filled from selected member"
                            persistent-hint
                          />
                        </v-col>
                      </v-row>

                      <v-divider class="mb-5 mt-1" />
                      <div class="text-overline grey--text text--darken-1 mb-3">Notes</div>

                      <v-row dense>
                        <v-col cols="12">
                          <v-textarea
                            v-model="manual.description"
                            outlined
                            dense
                            rows="3"
                            label="Description / Notes"
                            placeholder="e.g. Manual entry - cash payment received at counter"
                            counter="255"
                            :rules="[v => !v || v.length <= 255 || 'Max 255 characters']"
                          />
                        </v-col>
                      </v-row>

                      <v-divider class="mb-5 mt-1" />
                      <div class="text-overline grey--text text--darken-1 mb-3">Document</div>

                      <v-row dense>
                        <v-col cols="12">
                          <v-file-input
                            v-model="manual.document"
                            outlined
                            dense
                            label="Upload receipt / document"
                            accept=".pdf,.jpg,.jpeg,.png"
                            prepend-icon="mdi-paperclip"
                            show-size
                            clearable
                            hint="PDF, JPG, or PNG - max 5 MB"
                            persistent-hint
                            :rules="[v => !v || v.size <= 5 * 1024 * 1024 || 'File must be under 5 MB']"
                          />
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-card-text>
                </v-card>

                <v-alert v-if="message" dense outlined :type="messageType" class="mt-3" dismissible @input="message = ''">
                  {{ message }}
                </v-alert>
              </div>
            </v-col>
          </v-row>
      </v-container>
  </dashboard-layout>
</template>

<script>
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
const axios = require('axios')

export default {
  name: 'MOAManualPayment',

  components: {
    DashboardLayout,
    BaseBreadcrumb,
  },

  data() {
    return {
      page: { title: "Manual Payment" },
      breadcrumbs: [
        { text: "Dashboard", disabled: false, to: "/dashboard" },
        { text: "Payments", disabled: false, to: "/admin/payments" },
        { text: "Manual Payment", disabled: true },
      ],
      saving: false,
      loadingMembers: false,
      message: '',
      messageType: 'success',
      members: [],
      memberSearch: '',
      manualFormValid: false,
      rule: null,
      manual: {
        selectedMember: null,
        paymenttype: 'EFSF',
        payyear: new Date().getFullYear(),
        amount: '',
        paymentdate: new Date().toISOString().split('T')[0],
        trxnid: '',
        description: '',
        document: null,
      },
      paymentTypeOptions: [
        { value: 'EFSF', label: 'Entrance Fee + Annual Fee' },
        { value: 'SF', label: 'Annual Fee / Renewal' },
      ],
    }
  },

  methods: {
    showMessage(type, text) {
      this.messageType = type
      this.message = text
    },

    memberFilter(item, queryText) {
      const q = String(queryText || '').toLowerCase()
      return (
        String(item.fullname || '').toLowerCase().includes(q) ||
        String(item.memberid || '').toLowerCase().includes(q) ||
        String(item.icpassport || '').toLowerCase().includes(q) ||
        String(item.contactemail || '').toLowerCase().includes(q) ||
        String(item.status_label || '').toLowerCase().includes(q)
      )
    },

    clearMemberSelection() {
      this.manual.selectedMember = null
      this.memberSearch = ''
    },

    resetManualForm() {
      this.$refs.manualForm && this.$refs.manualForm.resetValidation()
      this.manual = {
        selectedMember: null,
        paymenttype: 'EFSF',
        payyear: this.rule ? this.rule.payyear : new Date().getFullYear(),
        amount: '',
        paymentdate: new Date().toISOString().split('T')[0],
        trxnid: '',
        description: '',
        document: null,
      }
      this.memberSearch = ''
    },

    async loadMembers() {
      this.loadingMembers = true
      try {
        const resp = await axios.get('/admin/members/list')
        this.members = resp.data && Array.isArray(resp.data.data) ? resp.data.data : []
      } finally {
        this.loadingMembers = false
      }
    },

    async loadRule() {
      try {
        const resp = await axios.get('/admin/payments/subscription-rule')
        this.rule = resp.data && resp.data.status === 'OK' ? resp.data.data : null
        if (this.rule) {
          this.manual.payyear = this.rule.payyear
        }
      } catch (e) {
        console.warn('subscription-rule not available', e.message)
      }
    },

    async submitManualPayment() {
      const valid = this.$refs.manualForm.validate()
      if (!valid) return
      if (!this.manual.selectedMember) {
        this.showMessage('error', 'Please select a member.')
        return
      }

      this.saving = true
      try {
        const formData = new FormData()
        formData.append('userid', this.manual.selectedMember.userid)
        formData.append('membertype', this.manual.selectedMember.membertype)
        formData.append('paymenttype', this.manual.paymenttype)
        formData.append('payyear', this.manual.payyear)
        formData.append('amount', parseFloat(this.manual.amount).toFixed(2))
        formData.append('paymentdate', this.manual.paymentdate)
        formData.append('trxnstatus', '01')
        if (this.manual.trxnid) formData.append('trxnid', this.manual.trxnid)
        formData.append('description', this.manual.description || this.selectedPaymentTypeLabel())
        if (this.manual.document) formData.append('document', this.manual.document)

        const resp = await axios.post('/admin/payments/manual', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        if (resp.data && resp.data.status === 'OK') {
          this.showMessage('success', resp.data.message || 'Payment recorded successfully.')
          if (resp.data.receipt) {
            this.downloadReceipt(resp.data.receipt)  // call it here
          }
          await this.loadMembers()
          this.resetManualForm()
        } else {
          this.showMessage('error', resp.data ? resp.data.error : 'Unable to record payment.')
        }
      } catch (e) {
        this.showMessage('error', e.message || 'Unexpected error.')
      } finally {
        this.saving = false
      }
    },

    downloadReceipt(r) {
      // Convert amount to words
      const amountWords = (amount) => {
        const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
                        'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN',
                        'SEVENTEEN', 'EIGHTEEN', 'NINETEEN']
        const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY']

        const toWords = (n) => {
              if (n === 0) return ''
              if (n < 20) return ones[n] + ' '
              if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' '
              if (n < 1000) return ones[Math.floor(n / 100)] + ' HUNDRED ' + toWords(n % 100)
              if (n < 1000000) return toWords(Math.floor(n / 1000)) + 'THOUSAND ' + toWords(n % 1000)
              return toWords(Math.floor(n / 1000000)) + 'MILLION ' + toWords(n % 1000000)
        }

        const [ringgit, sen] = parseFloat(amount).toFixed(2).split('.')
        const ringgitWords = toWords(parseInt(ringgit)).trim()
        const senWords = parseInt(sen) > 0 ? ' AND SEN ' + toWords(parseInt(sen)).trim() : ''
        return ringgitWords + senWords + ' ONLY'
      }

      const paymentTypeLabel = r.paymenttype === 'EFSF'
        ? 'Entrance Fee + Subscription Fee'
        : r.paymenttype === 'SF'
        ? 'Membership Subscription'
        : r.paymenttype

      const html = `
        <html>
        <head>
              <meta charset="utf-8">
              <title>Receipt ${r.receipt_no}</title>
              <style>
                  * { margin: 0; padding: 0; box-sizing: border-box; }
                  body {
                    font-family: Arial, sans-serif;
                    font-size: 8px;
                    padding: 46px 30px;
                    background: #fff;
                    color: #000;
                  }
                  .header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 28px;
                  }
                  .header-left {
                    display: flex;
                    align-items: flex-start;
                    gap: 8px;
                  }
                  .logo {
                    width: 76px;
                    height: 76px;
                  }
                  .org-name {
                    font-size: 9px;
                    font-weight: bold;
                    color: #000;
                    letter-spacing: 0;
                    white-space: nowrap;
                  }
                  .org-sub {
                    font-size: 8px;
                    font-weight: bold;
                    margin-top: 2px;
                  }
                  .org-address {
                    font-size: 8px;
                    margin-top: 3px;
                    line-height: 1.45;
                    color: #333;
                  }
                  .receipt-title {
                    text-align: center;
                    font-size: 8px;
                    font-weight: bold;
                    text-decoration: underline;
                    margin: 28px 0 22px;
                    letter-spacing: 0;
                  }
                  .received-text {
                    font-size: 11px;
                    line-height: 1.45;
                    margin-bottom: 22px;
                  }
                  .received-text b {
                    font-weight: bold;
                  }
                  .details-box {
                    border: 1px solid #cfd4dc;
                    padding: 8px 10px;
                    display: inline-block;
                    min-width: 235px;
                    font-size: 8px;
                    line-height: 1.6;
                  }
                  @media print {
                    @page { size: A4 portrait; margin: 0; }
                    body { padding: 46px 30px; }
                  }
              </style>
        </head>
        <body>
              <div class="header">
                  <div class="header-left">
                    <img class="logo" src="/images/miplogo-300x300.png"
                          onerror="this.style.display='none'" />
                    <div>
                          <div class="org-name">PERTUBUHAN PERANCANG MALAYSIA</div>
                          <div class="org-sub">MALAYSIAN INSTITUTE OF PLANNERS</div>
                          <div class="org-address">
                              B-1-02, JALAN SS7/13B, Plaza Kelana Jaya,<br>
                              47301 Petaling Jaya, Selangor.<br>
                              Tel. No: 03-78770636/6037 &nbsp;&nbsp; Fax No: 03-78779636
                          </div>
                    </div>
                  </div>
              </div>

              <div class="receipt-title">OFFICIAL RECEIPT</div>

              <div class="received-text">
                  Received from <b>${r.fullname} (${r.memberid})</b> the sum of RINGGIT MALAYSIA (RM)
                  <b>${parseFloat(r.amount).toFixed(2)} ${amountWords(r.amount)}</b><br>
                  being paid for <b>${paymentTypeLabel} for ${r.payyear}</b>
              </div>

              <div class="details-box">
                  <div><b>Amount:</b> RM ${parseFloat(r.amount).toFixed(2)}</div>
                  <div><b>Payment Date:</b> ${new Date(r.paymentdate).toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric' })}</div>
                  <div><b>Payment Ref. No:</b> ${r.trxnid}</div>
              </div>
        </body>
        </html>
      `

      const win = window.open('', '_blank', 'width=800,height=900')
      win.document.write(html)
      win.document.close()
      win.focus()
      setTimeout(() => win.print(), 600)
    },

    selectedPaymentTypeLabel() {
      const selected = this.paymentTypeOptions.find(item => item.value === this.manual.paymenttype)
      return selected ? selected.label : this.manual.paymenttype
    },
  },

  async mounted() {
    await this.loadRule()
    await this.loadMembers()
  },
}
</script>
